import React, { useState } from 'react';
import { Bookmark, Plus, Trash2, Check, X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DEFAULT_PRESETS = [
  {
    id: 'home-trips',
    name: 'Home Trips',
    description: 'Standard domestic flights with moderate alerts',
    icon: '🏠',
    config: {
      notifyFlightDelays: true,
      notifyGateChanges: true,
      notifyScheduleChanges: true,
      notifyCompliance: true,
      notifyPayDiscrepancies: true,
      notifyWeatherAlerts: false,
      enableQuietHours: true,
      quietHoursStart: '22:00',
      quietHoursEnd: '07:00',
      quietHoursCriticalOverride: true,
      notificationFrequency: 'realtime',
      delayAlertThreshold: '30',
      groupSimilarAlerts: true,
      enableSmsAlerts: false,
      smsCriticalThreshold: '60',
      smsBackupOnly: false,
      enablePushNotifications: true
    }
  },
  {
    id: 'international',
    name: 'International Routes',
    description: 'Long-haul flights with comprehensive monitoring',
    icon: '✈️',
    config: {
      notifyFlightDelays: true,
      notifyGateChanges: true,
      notifyScheduleChanges: true,
      notifyCompliance: true,
      notifyPayDiscrepancies: true,
      notifyWeatherAlerts: true,
      enableQuietHours: false,
      quietHoursStart: '22:00',
      quietHoursEnd: '07:00',
      quietHoursCriticalOverride: true,
      notificationFrequency: 'realtime',
      delayAlertThreshold: '15',
      groupSimilarAlerts: false,
      enableSmsAlerts: true,
      smsCriticalThreshold: '60',
      smsBackupOnly: false,
      enablePushNotifications: true
    }
  },
  {
    id: 'tight-connections',
    name: 'Tight Connections',
    description: 'Minimal connection times, maximum alerts',
    icon: '⚡',
    config: {
      notifyFlightDelays: true,
      notifyGateChanges: true,
      notifyScheduleChanges: true,
      notifyCompliance: true,
      notifyPayDiscrepancies: true,
      notifyWeatherAlerts: true,
      enableQuietHours: false,
      quietHoursStart: '22:00',
      quietHoursEnd: '07:00',
      quietHoursCriticalOverride: true,
      notificationFrequency: 'realtime',
      delayAlertThreshold: '0',
      groupSimilarAlerts: false,
      enableSmsAlerts: true,
      smsCriticalThreshold: '30',
      smsBackupOnly: false,
      enablePushNotifications: true
    }
  }
];

const NotificationPresets = ({ currentConfig, onApplyPreset, customPresets = [], onSaveCustomPreset, onDeleteCustomPreset }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDescription, setNewPresetDescription] = useState('');
  const [newPresetIcon, setNewPresetIcon] = useState('📋');

  const allPresets = [...DEFAULT_PRESETS, ...customPresets];

  const handleSaveNewPreset = () => {
    if (!newPresetName?.trim()) return;

    const newPreset = {
      id: `custom-${Date.now()}`,
      name: newPresetName,
      description: newPresetDescription,
      icon: newPresetIcon,
      config: currentConfig,
      isCustom: true
    };

    onSaveCustomPreset?.(newPreset);
    setIsCreating(false);
    setNewPresetName('');
    setNewPresetDescription('');
    setNewPresetIcon('📋');
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewPresetName('');
    setNewPresetDescription('');
    setNewPresetIcon('📋');
  };

  const iconOptions = [
    { value: '🏠', label: '🏠 Home' },
    { value: '✈️', label: '✈️ Plane' },
    { value: '⚡', label: '⚡ Fast' },
    { value: '🌍', label: '🌍 Globe' },
    { value: '🌙', label: '🌙 Night' },
    { value: '☀️', label: '☀️ Day' },
    { value: '📋', label: '📋 Clipboard' },
    { value: '⭐', label: '⭐ Star' },
    { value: '🔔', label: '🔔 Bell' },
    { value: '🚀', label: '🚀 Rocket' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bookmark className="w-6 h-6 text-accent-blue" />
          <div>
            <h2 className="text-xl font-semibold text-foreground">Notification Presets</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Quick-switch between notification configurations for different flight scenarios
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsCreating(true)}
          disabled={isCreating}
        >
          <Plus className="w-4 h-4 mr-2" />
          Save Current as Preset
        </Button>
      </div>

      {/* Create New Preset Form */}
      {isCreating && (
        <div className="bg-accent-blue/5 border border-accent-blue/20 rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Create New Preset</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-foreground mb-1">
                  Preset Name *
                </label>
                <Input
                  type="text"
                  value={newPresetName}
                  onChange={(e) => setNewPresetName(e?.target?.value)}
                  placeholder="e.g., Red-eye Flights"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  Icon
                </label>
                <Select
                  value={newPresetIcon}
                  onChange={(e) => setNewPresetIcon(e?.target?.value)}
                  options={iconOptions}
                  className="w-full"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">
                Description (Optional)
              </label>
              <Input
                type="text"
                value={newPresetDescription}
                onChange={(e) => setNewPresetDescription(e?.target?.value)}
                placeholder="Brief description of when to use this preset"
                className="w-full"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelCreate}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveNewPreset}
                disabled={!newPresetName?.trim()}
              >
                <Check className="w-4 h-4 mr-1" />
                Save Preset
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preset Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allPresets?.map((preset) => (
          <div
            key={preset?.id}
            className="bg-background border border-border rounded-lg p-4 hover:border-accent-blue/50 transition-colors cursor-pointer group"
            onClick={() => onApplyPreset?.(preset?.config)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{preset?.icon}</div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground group-hover:text-accent-blue transition-colors">
                    {preset?.name}
                  </h3>
                  {preset?.isCustom && (
                    <span className="text-xs text-accent-blue">Custom</span>
                  )}
                </div>
              </div>
              {preset?.isCustom && (
                <button
                  onClick={(e) => {
                    e?.stopPropagation();
                    onDeleteCustomPreset?.(preset?.id);
                  }}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-600 p-1"
                  title="Delete preset"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              {preset?.description}
            </p>
            <div className="flex flex-wrap gap-2">
              {preset?.config?.notificationFrequency === 'realtime' && (
                <span className="text-xs px-2 py-1 bg-accent-blue/10 text-accent-blue rounded">
                  Real-time
                </span>
              )}
              {preset?.config?.enableSmsAlerts && (
                <span className="text-xs px-2 py-1 bg-green-500/10 text-green-500 rounded">
                  SMS Enabled
                </span>
              )}
              {preset?.config?.enableQuietHours && (
                <span className="text-xs px-2 py-1 bg-purple-500/10 text-purple-500 rounded">
                  Quiet Hours
                </span>
              )}
              {preset?.config?.delayAlertThreshold === '0' && (
                <span className="text-xs px-2 py-1 bg-amber-500/10 text-amber-500 rounded">
                  All Delays
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {allPresets?.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <Bookmark className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No presets available. Create your first preset above.</p>
        </div>
      )}
    </div>
  );
};

export default NotificationPresets;